import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { RtlProvider } from '@/components/shared/rtl-provider';
import { BILLING_QUERY_KEYS } from '@/features/billing/query-keys';
import { NOTIFICATIONS_TOASTER_ID } from '@/features/notifications/components/show-notification-toast';
import { router } from '@/router';
import BProgressProvider from './components/ui/bg-progress';

/**
 * The refresh fired the instant a mutation settles is now the one that counts:
 * the API applies a release before it answers (GaugeBillingHook.settleRelease),
 * so the freed slot is already visible on the read that immediately follows.
 *
 * These rungs exist only for the degraded path — a runtime too slow to settle
 * inline hands off to the durable queue, and the count then moves at some
 * unknowable later moment with nothing on screen to trigger a re-read. That is
 * what "deleted it and it still says limit reached" looked like. Backstopped
 * server-side: `billingLimitsSettlingKey` pins the limits micro-cache to a 2s
 * TTL for that whole window, so no rung can read a long-lived stale body.
 */
const PLAN_USAGE_SETTLEMENT_LAG_MS = [1_200, 4_000, 9_000] as const;
let planUsageSettlementTimers: ReturnType<typeof setTimeout>[] = [];

const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        },
    },
    // One org-wide snapshot (`GET /billing/usage`) feeds every plan-limit
    // warning — app-shell banner, per-module usage chips, billing meters — and
    // the banner never unmounts, so a warning raised at the cap used to stay on
    // screen for the rest of the session. Mutations that move metered capacity
    // declare `meta: REFRESHES_PLAN_USAGE`; refreshing here (on failure too, so
    // a create rejected AT the cap repaints the meters) keeps that one snapshot
    // honest without repeating an invalidation in every feature.
    mutationCache: new MutationCache({
        onSettled: (_data, _error, _variables, _onMutateResult, mutation) => {
            if (mutation.meta?.refreshesPlanUsage !== true) return;
            const refresh = (): void => {
                void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEYS.usage() });
                // The slim member-readable projection feeds the limit locks and
                // chips — refresh it in lockstep so a create/delete raises or
                // clears a lock without waiting out the staleTime.
                void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEYS.limits() });
            };
            refresh();
            // Coalesced across a burst (bulk delete, rapid archives) so a busy
            // screen costs one ladder, not one per mutation.
            planUsageSettlementTimers.forEach(clearTimeout);
            planUsageSettlementTimers = PLAN_USAGE_SETTLEMENT_LAG_MS.map((delay) =>
                setTimeout(refresh, delay),
            );
        },
    }),
});

function App() {
    return (
        <BProgressProvider>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider attribute="class" defaultTheme="light" disableTransitionOnChange>
                    <RtlProvider>
                        <TooltipProvider>
                            <RouterProvider router={router} />
                        </TooltipProvider>
                    </RtlProvider>
                    <Toaster />
                    {/* Dedicated, expanded (un-stacked) top-right toaster for notification arrivals. */}
                    <Toaster id={NOTIFICATIONS_TOASTER_ID} position="top-right" expand />
                </ThemeProvider>
            </QueryClientProvider>
        </BProgressProvider>
    );
}

export default App;
